'use client';

import {
  createContext,
  createElement,
  useContext,
  useCallback,
  useMemo,
  useSyncExternalStore,
  type ReactNode,
} from 'react';

export interface PagePreferenceScope {
  userId: string;
  tenantId: string;
  pageId: string;
}

/** Apps can pass their existing preference hook's result instead of using local storage. */
export interface PageLayoutPreferences {
  ready: boolean;
  values: Readonly<Record<string, boolean>>;
  set: (id: string, value: boolean) => void;
  /** App adapters reset their visibility and expansion namespaces together. */
  reset: () => void;
}

/** Apps own loading, authentication and server writes. Snapshots are cached JSON or null while loading. */
export interface PagePreferenceStore {
  getSnapshot: (scope: PagePreferenceScope, namespace: string) => string | null;
  subscribe: (scope: PagePreferenceScope, namespace: string, notify: () => void) => () => void;
  set: (scope: PagePreferenceScope, namespace: string, id: string, value: boolean) => void;
  reset: (scope: PagePreferenceScope, namespace: string) => void;
}
const PreferenceStoreContext = createContext<PagePreferenceStore | null>(null);
export function PagePreferenceStoreProvider({
  store,
  children,
}: {
  store: PagePreferenceStore;
  children: ReactNode;
}) {
  return createElement(PreferenceStoreContext.Provider, { value: store }, children);
}

const changedEvent = 'statura-page-layout-change';
const unavailableStorage = new Map<string, string>();
const serverSnapshot = () => null;

export function pagePreferenceKey(scope: PagePreferenceScope, namespace: string): string {
  return `statura:layout:v1:${JSON.stringify([scope.tenantId, scope.userId, scope.pageId, namespace])}`;
}

/** null on the server: consumers render a neutral header until preferences resolve. */
export function usePageLayoutPreferences(
  scope: PagePreferenceScope,
  namespace: string,
  enabled = true,
): PageLayoutPreferences {
  const store = useContext(PreferenceStoreContext);
  const key = pagePreferenceKey(scope, namespace);
  // The key stabilises an inline scope object without losing any identity field.
  const stableScope = useMemo(() => ({ ...scope }), [key]);
  const read = useCallback(() => {
    if (!enabled) return '';
    if (store) return store.getSnapshot(stableScope, namespace);
    if (unavailableStorage.has(key)) return unavailableStorage.get(key)!;
    try {
      return window.localStorage.getItem(key) ?? '';
    } catch {
      return unavailableStorage.get(key) ?? '';
    }
  }, [key, enabled, store, stableScope, namespace]);
  const subscribe = useCallback(
    (notify: () => void) => {
      if (!enabled) return () => {};
      if (store) return store.subscribe(stableScope, namespace, notify);
      window.addEventListener('storage', notify);
      window.addEventListener(changedEvent, notify);
      return () => {
        window.removeEventListener('storage', notify);
        window.removeEventListener(changedEvent, notify);
      };
    },
    [enabled, store, stableScope, namespace],
  );
  const snapshot = useSyncExternalStore<string | null>(subscribe, read, serverSnapshot);
  const values = useMemo(() => {
    try {
      const parsed: unknown = JSON.parse(snapshot || '{}');
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
      return Object.fromEntries(
        Object.entries(parsed).filter(([, value]) => typeof value === 'boolean'),
      ) as Record<string, boolean>;
    } catch {
      return {};
    }
  }, [snapshot]);
  const write = useCallback(
    (value: string) => {
      if (!enabled) return;
      try {
        window.localStorage.setItem(key, value);
        unavailableStorage.delete(key);
      } catch {
        // Private mode/storage denial must not disable the controls for this session.
        unavailableStorage.set(key, value);
      }
      window.dispatchEvent(new Event(changedEvent));
    },
    [key, enabled],
  );
  return {
    ready: snapshot !== null,
    values,
    set: (id, value) => {
      if (!enabled) return;
      if (store) return store.set(stableScope, namespace, id, value);
      let current: Record<string, boolean> = {};
      try {
        current = JSON.parse(read() || '{}');
      } catch {
        /* Discard corrupt preferences. */
      }
      write(JSON.stringify({ ...current, [id]: value }));
    },
    reset: () => {
      if (!enabled) return;
      if (store) store.reset(stableScope, namespace);
      else write('{}');
    },
  };
}
